import { Link } from "react-router-dom";
import { Check, type LucideIcon } from "lucide-react";
import { TOUCH_TARGET } from "@/components/buttonStyles";

// The wizard's steps, in order (BRK-S300). Shared, in src/base rather than the
// wizard base (BRK-S421): the won-bid dialog draws the same stepper, and
// surety never imports src/base/wizard (BRK-S312's boundary). Not SubmissionFlowProgress: that
// one is the submission's fixed six-phase path, and a wizard's steps come from
// its definition. Not surety's StepIndicator either: the wizard imports no
// bond code. Each step reads done, current or upcoming, and the current one
// carries `aria-current="step"`.
//
// Every step reached so far is a link named by its title (BRK-S307); a step
// whose earlier steps aren't complete isn't, and neither is anything before
// the application exists. A step an earlier change left incomplete says
// "Needs attention".
//
// Each step's circle holds its icon, and a line joins the steps (BRK-S355;
// Nav, 2026-10-14: "we need a logo for each step on the header"). A step whose
// definition names no icon keeps its number, and a done one its check. The
// icons are decorative: a step's name is its title alone, and the captions
// under it sit outside its link.
//
// Mobile-first: a phone gets the circles alone, each still a link, with the
// step on screen named under the row; from md each step is a column with its
// name under its circle. The names stay for screen readers at every width.
//
// A step made of sub-steps (BRK-S387) lists them under the row while it is
// open, "Basic information · Characteristics & History", wrapping inside the
// stepper on a narrow screen. Each reached so far is a link, the one on screen
// marked, and one an earlier change left incomplete says so.

export type WizardStepState = "done" | "current" | "upcoming";

/** The open step's sub-steps, by the same rules as the steps (BRK-S387). */
export interface WizardSubsteps {
  titles: readonly string[];
  current: number;
  done: readonly boolean[];
  attention?: readonly boolean[];
  hrefs?: readonly (string | null)[];
}

const CIRCLE: Record<WizardStepState | "attention", string> = {
  current: "bg-brk-accent text-white ring-4 ring-brk-accent/20",
  done: "bg-brk-accent/10 text-brk-accent",
  attention:
    "border-[1.5px] border-amber-500 bg-amber-50 text-amber-700 dark:bg-amber-400/10 dark:text-amber-300",
  upcoming: "border border-brk-border bg-brk-panel text-brk-dim",
};

export function WizardStepper({
  titles,
  icons = [],
  current,
  done,
  attention = [],
  hrefs = [],
  substeps,
}: {
  titles: readonly string[];
  /** Per step: its icon, if its definition names one (BRK-S355). */
  icons?: readonly (LucideIcon | undefined)[];
  current: number;
  /** Per step: whether PMS already holds everything it asks for. */
  done: readonly boolean[];
  /** Per step: whether an earlier change left it incomplete (BRK-S307). */
  attention?: readonly boolean[];
  /** Per step: its URL while it can be opened, else null (BRK-S307). */
  hrefs?: readonly (string | null)[];
  /** The open step's sub-steps, when it has them (BRK-S387). */
  substeps?: WizardSubsteps;
}) {
  const stateOf = (i: number): WizardStepState =>
    i === current ? "current" : done[i] ? "done" : "upcoming";
  const last = titles.length - 1;
  return (
    <nav
      aria-label="Steps"
      className="rounded-lg border border-brk-border bg-brk-panel px-2 py-3 md:px-3 md:pb-3.5 md:pt-4"
    >
      <ol className="flex">
        {titles.map((title, i) => {
          const state = stateOf(i);
          const href = hrefs[i] ?? null;
          const flagged = attention[i] ?? false;
          const Icon = icons[i];
          const marker = (
            <span
              aria-hidden="true"
              className={`relative flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-caption md:h-10 md:w-10 ${
                CIRCLE[state === "current" || !flagged ? state : "attention"]
              }`}
            >
              {Icon ? (
                <Icon className="h-4 w-4 md:h-5 md:w-5" />
              ) : state === "done" ? (
                <Check size={14} />
              ) : (
                i + 1
              )}
              {Icon && state === "done" && (
                <span className="absolute -bottom-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full border-2 border-brk-panel bg-brk-accent text-white">
                  <Check size={8} strokeWidth={4} />
                </span>
              )}
              {flagged && (
                <span className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full border-2 border-brk-panel bg-amber-500" />
              )}
            </span>
          );
          // Every name stays for screen readers; a phone shows the circles.
          const name = (
            <span
              className={`sr-only text-meta md:not-sr-only ${
                state === "current"
                  ? "font-semibold text-brk-text"
                  : state === "done"
                    ? "font-medium text-brk-text group-hover:text-brk-accent"
                    : "text-brk-dim group-hover:text-brk-accent"
              }`}
            >
              {title}
            </span>
          );
          const column =
            "flex h-11 w-11 items-center justify-center md:h-auto md:w-auto md:flex-col md:justify-start md:gap-2 md:px-1";
          return (
            <li
              key={title}
              data-state={state}
              data-attention={flagged ? "true" : undefined}
              aria-current={state === "current" && !href ? "step" : undefined}
              className="relative flex min-w-0 flex-1 basis-0 flex-col items-center text-center"
            >
              {i < last && (
                <span
                  aria-hidden="true"
                  data-line={state === "done" ? "done" : "todo"}
                  className={`absolute left-[calc(50%_+_20px)] right-[calc(-50%_+_20px)] top-[21px] h-0.5 rounded-full md:left-[calc(50%_+_26px)] md:right-[calc(-50%_+_26px)] md:top-[19px] ${
                    state === "done" ? "bg-brk-accent" : "bg-brk-border"
                  }`}
                />
              )}
              {href ? (
                <Link
                  to={href}
                  aria-current={state === "current" ? "step" : undefined}
                  className={`group rounded-lg ${column}`}
                >
                  {marker}
                  {name}
                </Link>
              ) : (
                <span className={column}>
                  {marker}
                  {name}
                </span>
              )}
              {state === "done" && <span className="sr-only">(done)</span>}
              {state === "current" && (
                <span className="hidden text-caption font-medium text-brk-accent md:block">
                  Step {i + 1} of {titles.length}
                </span>
              )}
              {flagged && (
                <span className="sr-only text-caption font-medium text-amber-700 md:not-sr-only dark:text-amber-300">
                  Needs attention
                </span>
              )}
            </li>
          );
        })}
      </ol>
      {substeps && <SubstepList title={titles[current]} {...substeps} />}
      {/* A phone names the step on screen here; the list already says it to
          a screen reader. The name shares its text with the separator, so it
          is never a second element that reads as the step's name alone. */}
      <p
        aria-hidden="true"
        className="mt-1 text-center text-meta font-semibold text-brk-text md:hidden"
      >
        <span className="text-brk-accent">
          Step {current + 1} of {titles.length}
        </span>
        {` · ${titles[current]}`}
        {attention[current] && (
          <span className="text-amber-700 dark:text-amber-300">
            {" · Needs attention"}
          </span>
        )}
      </p>
    </nav>
  );
}

/** The open step's sub-steps, in a wrapping line under the steps. */
function SubstepList({
  title,
  titles,
  current,
  done,
  attention = [],
  hrefs = [],
}: WizardSubsteps & { title: string }) {
  return (
    <ol
      aria-label={`Parts of ${title}`}
      className="mt-2 flex flex-wrap items-center justify-center gap-y-1 border-t border-brk-border pt-2 text-meta"
    >
      {titles.map((name, i) => {
        const state: WizardStepState =
          i === current ? "current" : done[i] ? "done" : "upcoming";
        const href = hrefs[i] ?? null;
        const flagged = attention[i] ?? false;
        const look = `inline-flex items-center gap-1 rounded-md px-1.5 py-1 ${TOUCH_TARGET} ${
          state === "current"
            ? "font-semibold text-brk-accent"
            : flagged
              ? "font-medium text-amber-700 dark:text-amber-300"
              : state === "done"
                ? "font-medium text-brk-text hover:text-brk-accent"
                : "text-brk-dim"
        }`;
        const label = (
          <>
            {state === "done" && (
              <Check aria-hidden="true" className="h-3.5 w-3.5" />
            )}
            {name}
          </>
        );
        return (
          <li
            key={name}
            data-state={state}
            data-attention={flagged ? "true" : undefined}
            className="flex items-center"
          >
            {i > 0 && (
              <span aria-hidden="true" className="text-brk-dim">
                {" · "}
              </span>
            )}
            {href ? (
              <Link
                to={href}
                aria-current={state === "current" ? "step" : undefined}
                className={look}
              >
                {label}
              </Link>
            ) : (
              <span
                aria-current={state === "current" ? "step" : undefined}
                className={look}
              >
                {label}
              </span>
            )}
            {state === "done" && <span className="sr-only">(done)</span>}
            {flagged && <span className="sr-only">Needs attention</span>}
          </li>
        );
      })}
    </ol>
  );
}
